<template>
  <HrPageLayout
    title="Records Management"
    subtitle="Record and track staff files: available, missing, transferred or closed"
    icon="📁"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/dashboard' },
      { label: 'Staff Establishment' },
      { label: 'File Registry' }
    ]"
  >
    <template #header-actions>
      <button class="hr-btn hr-btn-primary" @click="openAddModal">
        <i class="bx bx-plus"></i> Add File Record
      </button>
    </template>

    <div class="file-registry-content">
      <!-- Stats -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-folder"></i></div>
          <p class="hr-metric-label">Total Records</p>
          <h2 class="hr-metric-value">{{ stats.total }}</h2>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
          <p class="hr-metric-label">Available</p>
          <h2 class="hr-metric-value">{{ stats.available }}</h2>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-error-circle"></i></div>
          <p class="hr-metric-label">Missing</p>
          <h2 class="hr-metric-value">{{ stats.missing }}</h2>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-transfer"></i></div>
          <p class="hr-metric-label">Transferred</p>
          <h2 class="hr-metric-value">{{ stats.transferred }}</h2>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-folder-minus"></i></div>
          <p class="hr-metric-label">Closed</p>
          <h2 class="hr-metric-value">{{ stats.closed }}</h2>
        </div>
      </div>

      <!-- Filters -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body hr-py-3">
          <div class="hr-filters-row">
            <div class="hr-search-wrap">
              <input
                v-model="search"
                type="text"
                class="hr-input"
                placeholder="Search by record number or employee name..."
                spellcheck="false"
                @input="debouncedLoad"
              >
              <i class="bx bx-search-alt search-icon"></i>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Status</label>
              <select v-model="filterStatus" @change="loadRecords" class="hr-select" style="min-width: 160px;">
                <option value="">All statuses</option>
                <option value="available">Available</option>
                <option value="missing">Missing</option>
                <option value="transferred">Transferred</option>
                <option value="closed">Closed</option>
              </select>
              <select v-model="filterDepartmentId" @change="loadRecords" class="hr-select" style="min-width: 220px;">
                <option value="">All departments</option>
                <option v-for="d in departments" :key="'fd-' + d.id" :value="String(d.id)">{{ d.departmentName }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="hr-chart-card">
        <div class="hr-chart-body">
          <div class="hr-table-wrapper">
            <table class="hr-table hr-table-hover hr-table-striped">
              <thead>
                <tr>
                  <th>Record Number</th>
                  <th>Employee</th>
                  <th>Emp No</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th>Status Date</th>
                  <th>File</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="8" class="hr-text-center hr-py-4">
                    <div class="hr-spinner"></div>
                  </td>
                </tr>
                <tr v-else-if="filteredRecords.length === 0">
                  <td colspan="8" class="hr-text-center hr-py-4 hr-text-muted">
                    No records found
                  </td>
                </tr>
                <tr v-for="row in filteredRecords" :key="row.id">
                  <td><strong>{{ row.record_number }}</strong></td>
                  <td>{{ row.employee_name || '—' }}</td>
                  <td><span class="hr-badge hr-badge-secondary">{{ row.emp_no || row.empNo || '—' }}</span></td>
                  <td>
                    <span :class="'hr-badge ' + statusBadgeClass(row.status)">{{ formatStatus(row.status) }}</span>
                  </td>
                  <td class="hr-text-muted hr-small">{{ (row.notes || '').slice(0, 50) }}{{ (row.notes && row.notes.length > 50) ? '…' : '' }}</td>
                  <td>{{ formatDate(row.status_date) }}</td>
                  <td>
                    <div v-if="row.pdf_path" class="hr-btn-group">
                      <button
                        type="button"
                        class="hr-btn hr-btn-sm hr-btn-secondary"
                        title="Open file"
                        @click="openRegistryFile(row)"
                      >
                        <i class="bx bx-show"></i> View
                      </button>
                      <button
                        type="button"
                        class="hr-btn hr-btn-sm hr-btn-info"
                        title="Download file"
                        @click="downloadRegistryFile(row)"
                      >
                        <i class="bx bx-download"></i> Download
                      </button>
                    </div>
                    <span v-else class="hr-text-muted hr-small">—</span>
                  </td>
                  <td>
                    <div class="hr-btn-group">
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="editRecord(row)" title="Edit">
                        <i class="bx bx-edit"></i> Edit Record
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Add/Edit Modal -->
      <ModalWrapper ref="formModal" :title="editingId ? 'Edit File Record' : 'Add File Record'" size="large">
        <template #body>
          <form @submit.prevent="saveRecord" class="hr-form-grid">
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Record Number <span class="hr-text-danger">*</span></label>
              <input
                v-model="form.record_number"
                type="text"
                class="hr-input"
                placeholder="e.g. HR-2024-001"
                required
                :disabled="!!editingId"
              >
              <small v-if="editingId" class="hr-text-muted">Record number cannot be changed after creation.</small>
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Employee <span class="hr-text-danger">*</span></label>
              <input
                v-model.trim="employeePickerSearch"
                type="text"
                class="hr-input hr-mb-2"
                placeholder="Search by employee number, national ID, or name..."
                :disabled="!!editingId"
              >
              <select
                v-model="form.employee_id"
                class="hr-select"
                required
                :disabled="!!editingId"
              >
                <option value="">Select employee</option>
                <option v-for="emp in filteredEmployeesForPicker" :key="emp.id" :value="emp.id">
                  {{ [emp.firstName, emp.middleName || emp.middle_name, emp.lastName].filter(Boolean).join(' ') }} ({{ emp.empNo || emp.emp_no || emp.employeeNo || emp.employee_number || emp.employeeNumber || emp.personalNo || emp.payrollNo || emp.nationalId || emp.national_id }})
                </option>
              </select>
              <small v-if="employeePickerSearch && !filteredEmployeesForPicker.length" class="hr-text-muted">
                No employee matches "{{ employeePickerSearch }}".
              </small>
              <small v-if="!employees.length && !loadingEmployees" class="hr-text-muted">No employees loaded. Try refreshing.</small>
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Status <span class="hr-text-danger">*</span></label>
              <select v-model="form.status" class="hr-select" required @change="onStatusChange">
                <option value="available">Available</option>
                <option value="missing">Missing</option>
                <option value="transferred">Transferred</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div v-if="form.status === 'transferred'" class="hr-filter-group span-full">
              <label class="hr-form-label">Transferred To</label>
              <input v-model="form.transferred_to" type="text" class="hr-input" placeholder="Where was the file transferred?">
            </div>
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Status Date</label>
              <input v-model="form.status_date" type="date" class="hr-input">
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Notes</label>
              <textarea v-model="form.notes" class="hr-textarea" rows="3" placeholder="Optional notes"></textarea>
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Employee file (PDF or ZIP)</label>
              <input
                ref="pdfFileInput"
                type="file"
                class="hr-input"
                accept=".pdf,.zip,application/pdf,application/zip,application/x-zip-compressed"
                @change="onPdfFileChange"
              >
              <small class="hr-text-muted hr-d-block hr-mt-1">
                Optional. PDF or ZIP only. You can add it when creating the record or later when editing. Replacing uploads a new file and removes the old one.
              </small>
              <div v-if="pdfFile" class="hr-mt-2">
                <span class="hr-badge hr-badge-success"><i class="bx bx-file"></i> {{ pdfFile.name }}</span>
                <button type="button" class="hr-btn hr-btn-link hr-btn-sm hr-ms-1" @click.prevent="clearPdfFile">Clear</button>
              </div>
              <div v-if="editingId && existingPdfPath && !pdfFile" class="hr-mt-2">
                <span class="hr-text-muted hr-small">A file is already stored for this record.</span>
                <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary hr-ms-2" @click.prevent="openRegistryFile({ id: editingId })">
                  <i class="bx bx-show"></i> View current file
                </button>
                <button type="button" class="hr-btn hr-btn-sm hr-btn-info hr-ms-2" @click.prevent="downloadRegistryFile({ id: editingId, record_number: form.record_number, pdf_path: existingPdfPath })">
                  <i class="bx bx-download"></i> Download current file
                </button>
                <button type="button" class="hr-btn hr-btn-sm hr-btn-danger hr-ms-2" @click.prevent="deleteRegistryFile">
                  <i class="bx bx-trash"></i> Delete current file
                </button>
              </div>
              <small v-if="editingId" class="hr-text-muted hr-d-block hr-mt-1">Upload a new file to replace the current one.</small>
            </div>
          </form>
        </template>
        <template #footer>
          <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.formModal?.close()">Cancel</button>
          <button type="button" class="hr-btn hr-btn-primary" @click="saveRecord" :disabled="saving">
            <span v-if="saving" class="hr-spinner small"></span>
            <i v-else class="bx bx-check"></i> {{ editingId ? 'Update' : 'Save' }}
          </button>
        </template>
      </ModalWrapper>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { execute, executeDownload, authUrl } from "@/api";

export default {
  name: "FileRegistry",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      records: [],
      employees: [],
      loading: false,
      loadingEmployees: false,
      saving: false,
      search: "",
      filterStatus: "",
      filterDepartmentId: "",
      employeePickerSearch: "",
      editingId: null,
      form: {
        record_number: "",
        employee_id: "",
        status: "available",
        notes: "",
        transferred_to: "",
        status_date: new Date().toISOString().slice(0, 10)
      },
      pdfFile: null,
      existingPdfPath: null,
      debounceTimer: null,
      employeeSearchTimer: null
    };
  },
  computed: {
    filteredEmployeesForPicker() {
      const list = Array.isArray(this.employees) ? this.employees : [];
      const q = (this.employeePickerSearch || "").trim().toLowerCase();
      if (!q) return list;
      return list.filter((emp) => {
        const searchable = this.buildEmployeeSearchText(emp);
        return searchable.includes(q);
      });
    },
    filteredRecords() {
      return this.records;
    },
    stats() {
      const s = { total: 0, available: 0, missing: 0, transferred: 0, closed: 0 };
      this.records.forEach((r) => {
        s.total++;
        const st = (r.status || "").toLowerCase();
        if (st === "available") s.available++;
        else if (st === "missing") s.missing++;
        else if (st === "transferred") s.transferred++;
        else if (st === "closed") s.closed++;
      });
      return s;
    }
  },
  mounted() {
    this.loadRecords();
  },
  watch: {
    employeePickerSearch() {
      this.debouncedEmployeeSearch();
    },
    employees() {
      this.autoSelectEmployeeFromSearch();
    }
  },
  methods: {
    debouncedLoad() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.loadRecords(), 300);
    },
    debouncedEmployeeSearch() {
      if (this.employeeSearchTimer) clearTimeout(this.employeeSearchTimer);
      this.employeeSearchTimer = setTimeout(() => {
        this.loadEmployees(this.employeePickerSearch);
      }, 300);
    },
    async loadRecords() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getStaffFileRegistry");
        data.append("keyword", this.search);
        if (this.filterStatus) data.append("status", this.filterStatus);
        if (this.filterDepartmentId) data.append("departmentId", this.filterDepartmentId);
        const res = await execute(data, authUrl);
        if (res?.data?.success && Array.isArray(res.data.data?.records)) {
          this.records = res.data.data.records;
        } else {
          this.records = res?.data?.records ?? [];
        }
      } catch (e) {
        console.error("File registry load error:", e);
        this.records = [];
      } finally {
        this.loading = false;
      }
    },
    async loadEmployees(keyword = "") {
      this.loadingEmployees = true;
      try {
        const data = new FormData();
        data.append("function", "getEmployees");
        data.append("page", "1");
        data.append("rows_per_page", "100");
        const searchText = String(keyword || "").trim();
        if (searchText) data.append("keyword", searchText);
        const res = await execute(data, authUrl);
        const list = res?.data?.employees ?? res?.data?.data?.employees ?? res?.data ?? [];
        this.employees = Array.isArray(list) ? list : [];
      } catch (e) {
        console.error("Employees load error:", e);
        this.employees = [];
      } finally {
        this.loadingEmployees = false;
        this.autoSelectEmployeeFromSearch();
      }
    },
    getEmployeeSearchValue(emp, key) {
      if (key === "employeeNo") {
        return String(
          emp?.empNo ||
          emp?.emp_no ||
          emp?.employeeNo ||
          emp?.employee_number ||
          emp?.employeeNumber ||
          emp?.personalNo ||
          emp?.payrollNo ||
          ""
        ).trim();
      }
      if (key === "nationalId") {
        return String(emp?.nationalId || emp?.national_id || "").trim();
      }
      if (key === "fullName") {
        const firstName = String(emp?.firstName || "").trim();
        const middleName = String(emp?.middleName || emp?.middle_name || "").trim();
        const lastName = String(emp?.lastName || "").trim();
        return `${firstName} ${middleName} ${lastName}`.replace(/\s+/g, " ").trim();
      }
      return "";
    },
    buildEmployeeSearchText(emp) {
      const fullName = this.getEmployeeSearchValue(emp, "fullName");
      const employeeNo = this.getEmployeeSearchValue(emp, "employeeNo");
      const nationalId = this.getEmployeeSearchValue(emp, "nationalId");
      const detailBits = [
        fullName,
        employeeNo,
        nationalId,
        emp?.email,
        emp?.phone,
        emp?.mobile,
        emp?.designation,
        emp?.position,
        emp?.departmentName,
        emp?.department_name
      ]
        .map((v) => String(v || "").trim())
        .filter(Boolean);
      return detailBits.join(" ").toLowerCase();
    },
    autoSelectEmployeeFromSearch() {
      if (this.editingId) return;
      const query = String(this.employeePickerSearch || "").trim();
      if (!query) return;

      const qLower = query.toLowerCase();
      const list = this.filteredEmployeesForPicker || [];
      if (!list.length) return;

      // Prefer exact employee number / national ID matches.
      const exactMatches = list.filter((emp) => {
        const employeeNo = this.getEmployeeSearchValue(emp, "employeeNo").toLowerCase();
        const nationalId = this.getEmployeeSearchValue(emp, "nationalId").toLowerCase();
        return employeeNo === qLower || nationalId === qLower;
      });

      if (exactMatches.length === 1) {
        this.form.employee_id = exactMatches[0].id;
        return;
      }

      // If there is only one filtered result, auto-select it.
      if (list.length === 1) {
        this.form.employee_id = list[0].id;
      }
    },
    openAddModal() {
      this.editingId = null;
      this.employeePickerSearch = "";
      this.pdfFile = null;
      this.existingPdfPath = null;
      this.form = {
        record_number: "",
        employee_id: "",
        status: "available",
        notes: "",
        transferred_to: "",
        status_date: new Date().toISOString().slice(0, 10)
      };
      this.loadEmployees("");
      this.$refs.formModal?.open();
      this.$nextTick(() => this.resetPdfInput());
    },
    editRecord(row) {
      this.editingId = row.id;
      this.employeePickerSearch = "";
      this.pdfFile = null;
      this.existingPdfPath = row.pdf_path || row.pdfPath || null;
      this.form = {
        record_number: row.record_number,
        employee_id: String(row.employee_id),
        status: (row.status || "available").toLowerCase(),
        notes: row.notes || "",
        transferred_to: row.transferred_to || "",
        status_date: row.status_date ? row.status_date.slice(0, 10) : new Date().toISOString().slice(0, 10)
      };
      if (!this.employees.length) this.loadEmployees("");
      this.$refs.formModal?.open();
      this.$nextTick(() => this.resetPdfInput());
    },
    onPdfFileChange(e) {
      const f = e.target.files && e.target.files[0];
      this.pdfFile = f || null;
    },
    clearPdfFile() {
      this.pdfFile = null;
      this.resetPdfInput();
    },
    resetPdfInput() {
      const el = this.$refs.pdfFileInput;
      if (el) el.value = "";
    },
    async openRegistryFile(row) {
      if (!row?.id) return;
      try {
        const data = new FormData();
        data.append("function", "downloadStaffFileRegistryPdf");
        data.append("id", String(row.id));
        const res = await executeDownload(data, authUrl);
        const blob = res.data;
        if (blob && blob.type && blob.type.indexOf("json") !== -1) {
          const text = await blob.text();
          try {
            const j = JSON.parse(text);
            window.alert(j.message || "Could not open file.");
          } catch (_) {
            window.alert("Could not open file.");
          }
          return;
        }
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      } catch (err) {
        window.alert(err?.message || "Could not open file.");
      }
    },
    async downloadRegistryFile(row) {
      if (!row?.id) return;
      try {
        const data = new FormData();
        data.append("function", "downloadStaffFileRegistryPdf");
        data.append("id", String(row.id));
        const res = await executeDownload(data, authUrl);
        const blob = res.data;
        if (blob && blob.type && blob.type.indexOf("json") !== -1) {
          const text = await blob.text();
          try {
            const j = JSON.parse(text);
            window.alert(j.message || "Could not download file.");
          } catch (_) {
            window.alert("Could not download file.");
          }
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const ext = ((row.pdf_path || "").toLowerCase().endsWith(".zip")) ? "zip" : "pdf";
        const fileName = `${(row.record_number || "record").replace(/[^A-Za-z0-9._-]/g, "_")}.${ext}`;
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      } catch (err) {
        window.alert(err?.message || "Could not download file.");
      }
    },
    async deleteRegistryFile() {
      if (!this.editingId) return;
      if (!window.confirm("Delete current uploaded file for this record?")) return;
      try {
        const data = new FormData();
        data.append("function", "deleteStaffFileRegistryPdf");
        data.append("id", String(this.editingId));
        const res = await execute(data, authUrl);
        if (res?.data?.success === false) {
          window.alert(res?.data?.message || "Could not delete file.");
          return;
        }
        this.existingPdfPath = null;
        await this.loadRecords();
      } catch (err) {
        window.alert(err?.message || "Could not delete file.");
      }
    },
    onStatusChange() {
      if (this.form.status !== "transferred") this.form.transferred_to = "";
    },
    async saveRecord() {
      if (!this.form.record_number || !this.form.employee_id || !this.form.status) return;
      this.saving = true;
      try {
        const data = new FormData();
        data.append("function", "saveStaffFileRegistry");
        data.append("record_number", this.form.record_number);
        data.append("employee_id", this.form.employee_id);
        data.append("status", this.form.status);
        data.append("notes", this.form.notes);
        data.append("transferred_to", this.form.transferred_to || "");
        data.append("status_date", this.form.status_date || "");
        if (this.editingId) data.append("id", String(this.editingId));
        if (this.pdfFile) {
          data.append("pdfFile", this.pdfFile);
        }
        const res = await execute(data, authUrl);
        if (res?.data?.success !== false) {
          this.pdfFile = null;
          this.resetPdfInput();
          this.$refs.formModal?.close();
          this.loadRecords();
        } else {
          alert(res?.data?.message || "Failed to save.");
        }
      } catch (e) {
        console.error("Save error:", e);
        alert(e?.message || "Failed to save.");
      } finally {
        this.saving = false;
      }
    },
    formatStatus(s) {
      if (!s) return "—";
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    },
    statusBadgeClass(s) {
      const t = (s || "").toLowerCase();
      if (t === "available") return "hr-badge-success";
      if (t === "missing") return "hr-badge-danger";
      if (t === "transferred") return "hr-badge-warning";
      if (t === "closed") return "hr-badge-secondary";
      return "hr-badge-info";
    },
    formatDate(d) {
      if (!d) return "—";
      return d.slice(0, 10);
    },
    goToEmployee(employeeId) {
      if (employeeId) {
        sessionStorage.setItem("employeeId", employeeId);
        this.$router.push("/employee-details-view");
      }
    }
  }
};
</script>

<style scoped>
.file-registry-content { max-width: 100%; }
.hr-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.span-half { grid-column: span 1; }
.span-full { grid-column: 1 / -1; }
</style>
